import React from "react";
import FolderSpecialOutlinedIcon from "@mui/icons-material/FolderSpecialOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";

// Thin-stroke nav icons from the 2a mockup (24px grid, 1.8 stroke) — shared
// by the workspace sidebar and the project sidebar so both rails match.
const StrokeIcon = ({ children }: { children: React.ReactNode }) => (
  <svg
    width={19}
    height={19}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

export const navIcons = {
  dashboard: (
    <StrokeIcon>
      <rect x={3} y={3} width={7} height={7} rx={1.5} />
      <rect x={14} y={3} width={7} height={7} rx={1.5} />
      <rect x={3} y={14} width={7} height={7} rx={1.5} />
      <rect x={14} y={14} width={7} height={7} rx={1.5} />
    </StrokeIcon>
  ),
  projects: (
    <StrokeIcon>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </StrokeIcon>
  ),
  team: (
    <StrokeIcon>
      <circle cx={9} cy={8} r={3.2} />
      <path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path d="M16 15.4c2.4.4 4.5 2.2 4.5 4.6" />
      <path d="M15 5.2a3.2 3.2 0 0 1 0 5.6" />
    </StrokeIcon>
  ),
  // Team drill-down (TeamSidebarNav); Members reuses `team`.
  billing: (
    <StrokeIcon>
      <rect x={3} y={5} width={18} height={14} rx={2} />
      <path d="M3 10h18" />
      <path d="M6.5 15h4" />
    </StrokeIcon>
  ),
  credits: (
    <StrokeIcon>
      <path d="M13.5 3 5.5 13.5h6l-1.5 7.5 8-10.5h-6z" />
    </StrokeIcon>
  ),
  settings: (
    <StrokeIcon>
      <path d="M4 7h9.5M18.5 7H20M4 17h1.5M10.5 17H20" />
      <circle cx={16} cy={7} r={2.5} />
      <circle cx={8} cy={17} r={2.5} />
    </StrokeIcon>
  ),
  library: (
    <StrokeIcon>
      <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" />
      <path d="M4 7.5l8 4.5 8-4.5" />
      <path d="M12 12v9" />
    </StrokeIcon>
  ),
  studio: (
    <StrokeIcon>
      <path d="M6 4h12v17l-6-4-6 4z" />
    </StrokeIcon>
  ),
  // Matches the Creator rail's "Library" icon (StyleOutlinedIcon) so the
  // Mattoboard catalog reads as the same place in both navs (#4022).
  matto: <StyleOutlinedIcon sx={{ fontSize: 20 }} />,
  // Matches the Creator rail's "Images" icon (ImageOutlinedIcon) — used only
  // by Reference Images; My Library has its own icon below (#4022).
  images: <ImageOutlinedIcon sx={{ fontSize: 20 }} />,
  myLibrary: <FolderSpecialOutlinedIcon sx={{ fontSize: 20 }} />,
  boards: (
    <StrokeIcon>
      <rect x={3} y={4} width={18} height={16} rx={2} />
      <path d="M9.5 4v16" />
      <path d="M15.5 4v16" />
    </StrokeIcon>
  ),
  // Image Edit (CREATE group): upload-and-edit concepts.
  imageEdit: <AutoFixHighOutlinedIcon sx={{ fontSize: 20 }} />,
  concepts: (
    <StrokeIcon>
      <rect x={4} y={4} width={13} height={13} rx={2} />
      <path d="M19 7l1.2 2.8L23 11l-2.8 1.2L19 15l-1.2-2.8L15 11l2.8-1.2z" />
    </StrokeIcon>
  ),
  // Matches the Creator rail's "Collections" icon (FolderOpenRoundedIcon),
  // the folder shape, instead of the old overlapping-squares glyph (#4022).
  collections: <FolderOpenRoundedIcon sx={{ fontSize: 20 }} />,
  overview: (
    <StrokeIcon>
      <path d="M4 11l8-6 8 6" />
      <path d="M6 10v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9" />
    </StrokeIcon>
  ),
  showrooms: (
    <StrokeIcon>
      <path d="M4 9.5 5.2 4h13.6L20 9.5" />
      <path d="M4 9.5a2.7 2.7 0 0 0 5.3 0 2.7 2.7 0 0 0 5.4 0 2.7 2.7 0 0 0 5.3 0" />
      <path d="M5.5 12v8h13v-8" />
      <path d="M10 20v-4.5h4V20" />
    </StrokeIcon>
  ),
  schedules: (
    <StrokeIcon>
      <rect x={3} y={4} width={18} height={17} rx={2} />
      <path d="M3 9h18" />
      <path d="M8 2v4" />
      <path d="M16 2v4" />
    </StrokeIcon>
  ),
};

// Group label style shared by the WORKSPACE / CREATE / THIS PROJECT
// subheaders (2a).
export const navSubheaderSx = {
  px: 1.25,
  lineHeight: "18px",
  mb: "4px",
  fontSize: "11.5px",
  letterSpacing: "0.08em",
  fontWeight: 700,
  color: "#a1a1aa",
  bgcolor: "transparent",
};

// Connector line that wraps only a group's rows (left offset varies by
// nesting depth).
export const connectorSx = (left: string) => ({
  position: "absolute" as const,
  left,
  top: "2px",
  bottom: "6px",
  width: "1px",
  bgcolor: "#E7E5E4",
});

// 2a nav row styling shared by the workspace and project sidebars. The
// renderers keep their own trailing content (chevron, counts, chips).
export const navRowSx = ({
  collapsed,
  indented,
  height = 38,
}: {
  collapsed: boolean;
  indented?: boolean;
  height?: number;
}) => ({
  borderRadius: "8px",
  height,
  px: 1.25,
  ...(indented && !collapsed && { pl: "34px" }),
  mb: "2px",
  justifyContent: collapsed ? "center" : "flex-start",
  "&:hover": { backgroundColor: "#F4F4F5" },
  "&.Mui-selected": {
    backgroundColor: "rgba(99,102,241,0.12)",
    "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
      color: "primary.main",
    },
  },
});

export const navRowIconSx = (isActive: boolean, collapsed: boolean) => ({
  color: isActive ? "primary.main" : "#1a1a1a",
  minWidth: 0,
  mr: collapsed ? 0 : "12px",
  justifyContent: "center",
});

export const navRowTextProps = {
  sx: { fontSize: "15px", fontWeight: 600, color: "#1a1a1a" },
  noWrap: true,
};
